import { FC, forwardRef, useContext, useImperativeHandle, useState } from 'react';
import { Form, Modal } from 'antd';
import dayjs from 'dayjs';
import { isArray } from '@lhb/func';
import { useMethods } from '@lhb/hook';
import { showInvalidFieldMsg } from '@/common/utils/ways';
import { postFranchiseeFollowRecordAdd } from '@/common/api/expandStore/franchisee';
import V2Form from '@/common/components/Form/V2Form';
import V2FormDatePicker from '@/common/components/Form/V2FormDatePicker/V2FormDatePicker';
import V2FormInput from '@/common/components/Form/V2FormInput/V2FormInput';
import V2FormSelect from '@/common/components/Form/V2FormSelect/V2FormSelect';
import V2FormTextArea from '@/common/components/Form/V2FormTextArea/V2FormTextArea';
import V2FormUpload from '@/common/components/Form/V2FormUpload/V2FormUpload';
import V2Message from '@/common/components/Others/V2Hint/V2Message';
import UserInfoContext from '@/layout/context';

/** 沟通类型选项 */
const FOLLOW_TYPE_OPTIONS = [
  { value: 1, label: '驱动开店' },
  { value: 2, label: '匹配进度' },
  { value: 3, label: '日常回访' },
  { value: 4, label: '其他事项' },
];

/** 沟通凭证：图片最多张数 */
// const IMAGE_MAX_COUNT = 9;
/** 沟通凭证：文件最多个数 */
const FILE_MAX_COUNT = 14;
/** 沟通凭证：单个文件大小上限，单位 M */
const FILE_MAX_SIZE = 50;

/**
 * 加盟商详情-写跟进弹窗
 */
const FollowRecordModal: FC<{
  /** 加盟商id */
  franchiseeId: number;
  /** 新增成功回调 */
  onSuccess?: () => void;
  ref?: any;
}> = forwardRef(({
  franchiseeId,
  onSuccess,
}, ref) => {

  useImperativeHandle(ref, () => ({
    init: methods.init,
  }));

  const [form] = Form.useForm();
  const [visible, setVisible] = useState<boolean>(false);
  const [requesting, setRequesting] = useState<boolean>(false);
  const userInfo: any = useContext(UserInfoContext); // 当前登录人信息

  const methods = useMethods({
    /** 打开弹窗：重置表单，默认跟进时间为当天，跟进人为当前登录人 */
    init() {
      setVisible(true);
      form.resetFields();
      form.setFieldsValue({
        followDate: dayjs().format('YYYY-MM-DD'),
        followUser: userInfo?.name,
      });
    },

    /** 关闭弹窗 */
    close() {
      setVisible(false);
    },

    /** 跟进时间不允许选择未来日期 */
    disabledDate(current: any) {
      return !!current && current.isAfter(dayjs(), 'day');
    },

    /** 提交跟进记录 */
    confirm() {
      form.validateFields().then((values: any) => {
        if (requesting) return;

        // const imageList = isArray(values.imageList) ? values.imageList.map((item) => item.url) : [];
        const fileList = isArray(values.fileList) ? values.fileList.map(({ name, url, size }) => ({ name, url, size })) : [];

        setRequesting(true);
        postFranchiseeFollowRecordAdd({
          franchiseeId,
          followDate: values.followDate,
          followType: values.followType,
          content: values.content,
          // imageList,
          fileList,
        }).then((res) => {
          if (!res) return;
          V2Message.success('新增跟进成功');
          setVisible(false);
          onSuccess?.();
        }).finally(() => {
          setRequesting(false);
        });
      }).catch((err) => {
        err && showInvalidFieldMsg(err.errorFields, 1);
      });
    },
  });

  return (
    <Modal
      title='写跟进'
      width={520}
      open={visible}
      destroyOnClose
      forceRender
      onCancel={methods.close}
      onOk={methods.confirm}
      confirmLoading={requesting}
    >
      <V2Form form={form}>
        <V2FormDatePicker
          required
          label='跟进时间'
          name='followDate'
          config={{
            format: 'YYYY-MM-DD',
            disabledDate: methods.disabledDate,
          }}
          formItemConfig={{
            getValueFromEvent: (...[, dateString]) => dateString,
            getValueProps: (value) => ({ value: value ? dayjs(value, 'YYYY-MM-DD') : undefined }),
          }}
        />
        <V2FormInput
          label='跟进人'
          name='followUser'
          disabled
        />
        <V2FormSelect
          required
          label='沟通类型'
          name='followType'
          options={FOLLOW_TYPE_OPTIONS}
        />
        <V2FormTextArea
          required
          label='沟通纪要'
          name='content'
          maxLength={200}
          config={{ showCount: true }}
        />
        {/* <V2FormUpload
          label='沟通凭证-图片'
          name='imageList'
          uploadType='image'
          config={{
            maxCount: IMAGE_MAX_COUNT,
            size: FILE_MAX_SIZE,
            fileType: ['png', 'jpg', 'jpeg', 'bmp', 'webp'],
          }}
          formItemConfig={{
            help: `支持 png / jpg / jpeg / bmp / webp 格式，最多${IMAGE_MAX_COUNT}张，单张不超过${FILE_MAX_SIZE}MB`,
          }}
          tipConfig={{ tooltipIconShow: false }}
          promptFileAllSuccess
        /> */}
        <V2FormUpload
          label='沟通凭证'
          name='fileList'
          config={{
            maxCount: FILE_MAX_COUNT,
            size: FILE_MAX_SIZE,
            fileType: ['png', 'jpg', 'jpeg', 'bmp', 'webp', 'pdf', 'doc', 'docx', 'xls', 'xlsx', 'ppt', 'pptx'],
          }}
          formItemConfig={{
            // help: `最多${FILE_MAX_COUNT}个，单个不超过${FILE_MAX_SIZE}MB`,
          }}
          // tipConfig={{ tooltipIconShow: false }}
          promptFileAllSuccess
        />
      </V2Form>
    </Modal>
  );
});

export default FollowRecordModal;
